import type {
  ColumnFiltersState,
  ColumnOrderState,
  ColumnSizingState,
  ColumnVisibilityState,
  ExpandedState,
  PaginationState,
  RowSelectionState,
  SortingState,
  Updater,
} from '@tanstack/react-table';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

const resolveUpdater = <T,>(updater: Updater<T>, current: T): T =>
  typeof updater === 'function' ? (updater as (prev: T) => T)(current) : updater;

export type TableStoreProps = {
  initialFilters?: ColumnFiltersState;
  initialPagination?: PaginationState;
  initialSorting?: SortingState;
  initialColumnVisibility?: ColumnVisibilityState;
  initialColumnOrder?: ColumnOrderState;
  name: string;
  version?: number;
};

export type State = {
  expanded: ExpandedState;
  filters: ColumnFiltersState;
  pagination: PaginationState;
  rowSelection: RowSelectionState;
  sorting: SortingState;
  columnVisibility: ColumnVisibilityState;
  columnSizing: ColumnSizingState;
  columnOrder: ColumnOrderState;
};

export type Action = {
  setExpanded: (updater: Updater<ExpandedState>) => void;
  setFilters: (updater: Updater<ColumnFiltersState>) => void;
  setPagination: (updater: Updater<PaginationState>) => void;
  setRowSelection: (updater: Updater<RowSelectionState>) => void;
  setSorting: (updater: Updater<SortingState>) => void;
  setColumnVisibility: (updater: Updater<ColumnVisibilityState>) => void;
  setColumnSizing: (updater: Updater<ColumnSizingState>) => void;
  setColumnOrder: (updater: Updater<ColumnOrderState>) => void;
  resetTable: () => void;
};

export type TableStore = ReturnType<typeof createTableStore>;

export const createTableStore = ({
  name,
  initialSorting = [],
  initialFilters = [],
  initialColumnVisibility = {},
  initialColumnOrder = [],
  initialPagination = { pageIndex: 0, pageSize: 15 },
  version = 6,
}: TableStoreProps) =>
  create<State & Action>()(
    persist(
      (set) => ({
        columnOrder: structuredClone(initialColumnOrder),
        columnSizing: {},
        columnVisibility: structuredClone(initialColumnVisibility),
        expanded: {},
        filters: structuredClone(initialFilters),
        pagination: structuredClone(initialPagination),
        rowSelection: {},
        resetTable: () => {
          set({
            columnOrder: structuredClone(initialColumnOrder),
            columnSizing: {},
            columnVisibility: structuredClone(initialColumnVisibility),
            expanded: {},
            filters: structuredClone(initialFilters),
            pagination: structuredClone(initialPagination),
            rowSelection: {},
            sorting: structuredClone(initialSorting),
          });
        },
        setColumnOrder: (updater) =>
          set((s) => ({ columnOrder: resolveUpdater(updater, s.columnOrder) })),
        setColumnSizing: (updater) =>
          set((s) => ({ columnSizing: resolveUpdater(updater, s.columnSizing) })),
        setColumnVisibility: (updater) =>
          set((s) => ({ columnVisibility: resolveUpdater(updater, s.columnVisibility) })),
        setExpanded: (updater) => set((s) => ({ expanded: resolveUpdater(updater, s.expanded) })),
        setFilters: (updater) => set((s) => ({ filters: resolveUpdater(updater, s.filters) })),
        setPagination: (updater) =>
          set((s) => ({ pagination: resolveUpdater(updater, s.pagination) })),
        setRowSelection: (updater) =>
          set((s) => ({ rowSelection: resolveUpdater(updater, s.rowSelection) })),
        setSorting: (updater) => set((s) => ({ sorting: resolveUpdater(updater, s.sorting) })),
        sorting: structuredClone(initialSorting),
      }),
      {
        name,
        version,
        partialize: ({ columnOrder, columnSizing, columnVisibility, expanded }) => ({
          columnOrder,
          columnSizing,
          columnVisibility,
          expanded,
        }),
        migrate: (persistedState) => {
          const persisted = persistedState as Partial<State>;
          return {
            columnOrder: persisted.columnOrder ?? structuredClone(initialColumnOrder),
            columnSizing: persisted.columnSizing ?? {},
            columnVisibility:
              persisted.columnVisibility ?? structuredClone(initialColumnVisibility),
            expanded: persisted.expanded ?? {},
          };
        },
      }
    )
  );
